const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const TWELVE_HOUR = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i;
const TWENTY_FOUR_HOUR = /^(\d{1,2}):(\d{2})$/;

export const MAX_SEATING_TIMES = 12;

export function isValidSeatingTime(value: string): boolean {
  return HHMM.test(value);
}

function toHHMM(entry: string): string {
  const trimmed = entry.trim();
  const twelve = trimmed.match(TWELVE_HOUR);
  if (twelve) {
    let hours = Number(twelve[1]);
    const minutes = Number(twelve[2] ?? "0");
    const meridiem = twelve[3].toLowerCase();
    if (hours < 1 || hours > 12 || minutes > 59) {
      throw new Error(`Seating time "${entry}" is not a valid time.`);
    }
    if (meridiem === "pm" && hours !== 12) hours += 12;
    if (meridiem === "am" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }
  const twentyFour = trimmed.match(TWENTY_FOUR_HOUR);
  if (twentyFour) {
    const candidate = `${twentyFour[1].padStart(2, "0")}:${twentyFour[2]}`;
    if (isValidSeatingTime(candidate)) return candidate;
  }
  throw new Error(`Seating time "${entry}" is not a valid time.`);
}

export function parseSeatingTimesInput(raw: string): string[] {
  const entries = raw
    .split(/[,\n]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  const unique = Array.from(new Set(entries.map(toHHMM)));
  if (unique.length > MAX_SEATING_TIMES) {
    throw new Error(`At most ${MAX_SEATING_TIMES} seating times are allowed.`);
  }
  return unique.sort();
}

export function formatSeatingTime(value: string): string {
  const [h, m] = value.split(":").map(Number);
  const meridiem = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${meridiem}`;
}

export function seatingTimesToInput(times: string[] | undefined): string {
  return (times ?? []).map(formatSeatingTime).join(", ");
}
